import React from "react";
import {
  Phone, Droplets, Wind, MoveRight, ShieldCheck, Truck, Warehouse, Wrench, Sparkles,
  MessageCircle, Send, Snowflake,
} from "lucide-react";
import { MODELS, telHref, telegramLink } from "../lib/config";
import { trackCall, trackEvent } from "../lib/analytics";

const CallBtn = ({ source, lang, T, model, segment, label, big = false, testId }) => (
  <a
    href={telHref}
    onClick={() => trackCall(source, { model, segment, language: lang })}
    data-testid={testId}
    data-event="ClickCall"
    data-source={source}
    data-model={model || ""}
    data-segment={segment || ""}
    data-language={lang}
    className={`inline-flex items-center justify-center gap-2 btn-call rounded-full font-bold transition shadow-flame ${
      big ? "px-7 py-4 text-lg" : "px-5 py-3 text-sm"
    }`}
  >
    <Phone size={big ? 20 : 16} strokeWidth={2.6} />
    <span>{label || T.cta.callAvailability[lang]}</span>
  </a>
);

/* ============ URGENCY STRIP ============ */
function UrgencyStrip({ lang, T }) {
  return (
    <section className="relative py-6 md:py-7">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative glass rounded-3xl px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between overflow-hidden">
          <div className="absolute inset-0 marquee" />
          <div className="relative flex items-start md:items-center gap-3">
            <div className="w-10 h-10 rounded-full grid place-items-center bg-danger/15 border border-danger/30">
              <Phone size={18} className="text-danger" />
            </div>
            <p className="text-white text-sm md:text-base">
              {T.urgencyStrip.text[lang]}
            </p>
          </div>
          <CallBtn lang={lang} T={T} source="urgency_strip" label={T.cta.callManager[lang]} testId="urgency-call-button" />
        </div>
      </div>
    </section>
  );
}

/* ============ PROBLEM ============ */
function Problem({ lang, T }) {
  const list = T.problem.list[lang];
  return (
    <section id="problem" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 reveal">
          <div className="relative rounded-[28px] overflow-hidden glass">
            <img
              src="/images/aircooler/3.png"
              loading="lazy"
              alt={lang === "uz" ? "Zamonaviy mehmonxonada mobil havo sovutgich" : "Мобильный охладитель воздуха в современной гостиной"}
              className="w-full h-auto object-cover"
              data-testid="problem-image"
            />
          </div>
        </div>
        <div className="lg:col-span-7 reveal">
          <span className="chip mb-4">PROBLEM</span>
          <h2 className="font-display font-bold text-3xl md:text-5xl leading-tight tracking-tight">
            {T.problem.h2[lang]}
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {list.map((l, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-2xl p-4 card-hover"
                data-testid={`problem-card-${i}`}
              >
                <span className="w-7 h-7 shrink-0 grid place-items-center rounded-full bg-danger/15 text-danger text-xs font-bold">
                  {i + 1}
                </span>
                <span className="text-white/90 text-[15px]">{l}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <CallBtn lang={lang} T={T} source="problem" testId="problem-call-button" />
            <button
              onClick={() => trackEvent("QuizStart", { source: "problem" }) || document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="problem-quiz-button"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              <Sparkles size={16} className="text-ice-300" />
              {T.cta.pickModel[lang]}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ HOW IT WORKS ============ */
function HowItWorks({ lang, T }) {
  const benefits = T.how.bullets[lang];
  return (
    <section id="how" className="relative py-16 md:py-24 bg-ink-800/40">
      <div className="mx-auto max-w-7xl px-4 md:px-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 reveal">
          <span className="chip mb-4">HOW IT WORKS</span>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{T.how.h2[lang]}</h2>
          <p className="mt-5 text-muted text-[16px] md:text-lg leading-relaxed max-w-2xl">{T.how.text[lang]}</p>

          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-2xl px-4 py-3 card-hover"
                data-testid={`how-benefit-${i}`}
              >
                <span className="w-9 h-9 rounded-xl grid place-items-center bg-ice-400/15 text-ice-300">
                  {[<Droplets size={18} />, <MoveRight size={18} />, <Wrench size={18} />, <Warehouse size={18} />, <Wind size={18} />][i % 5]}
                </span>
                <span className="text-white/90 text-sm">{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <CallBtn lang={lang} T={T} source="how" label={T.cta.callConsult[lang]} testId="how-call-button" />
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-cols-2 gap-4 reveal">
          <div className="col-span-2 relative rounded-[28px] overflow-hidden glass">
            <img
              src="/images/aircooler/9.png"
              loading="lazy"
              alt={lang === "uz" ? "Bug'lanish asosida ishlaydigan havo sovutgich sxemasi" : "Схема работы испарительного охладителя воздуха"}
              className="w-full h-auto object-cover"
              data-testid="how-scheme-image"
            />
            <div className="absolute top-3 left-3 chip" style={{ background: "rgba(34,211,238,.1)", borderColor: "rgba(34,211,238,.3)", color: "#22D3EE" }}>
              <Snowflake size={12} /> {lang === "uz" ? "Suv + havo" : "Вода + воздух"}
            </div>
          </div>
          <div className="relative rounded-[24px] overflow-hidden glass">
            <img src="/images/aircooler/5.png" loading="lazy" alt="" className="w-full h-40 object-cover" />
          </div>
          <div className="relative rounded-[24px] overflow-hidden glass">
            <img src="/images/aircooler/10.png" loading="lazy" alt="" className="w-full h-40 object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ SEGMENT SELECTOR ============ */
function Segments({ lang, T }) {
  const items = T.segments.items;
  return (
    <section id="segments" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="reveal">
            <span className="chip mb-3">SEGMENTS</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{T.segments.h2[lang]}</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((seg, i) => (
            <article
              key={seg.key}
              className="reveal relative rounded-[24px] overflow-hidden bg-ink-500 border border-white/5 card-hover group"
              data-testid={`segment-card-${seg.key}`}
            >
              <div className="relative h-56">
                <img src={seg.image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/60 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                  <h3 className="font-display font-bold text-2xl">{seg.title[lang]}</h3>
                  <span className="chip">{seg.rec}</span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-muted text-sm leading-relaxed">{seg.desc[lang]}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <CallBtn
                    lang={lang} T={T} source="segment" segment={seg.key}
                    label={T.cta.callShort[lang]}
                    testId={`segment-call-${seg.key}`}
                  />
                  <button
                    onClick={() => { trackEvent("QuizStart", { source: "segment", segment: seg.key }); document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" }); }}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs bg-white/5 border border-white/10 hover:bg-white/10 transition"
                    data-testid={`segment-quiz-${seg.key}`}
                  >
                    <Sparkles size={12} className="text-ice-300" />
                    {T.cta.pickModel[lang]}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ MODELS ============ */
function Models({ lang, T }) {
  const tierLabel = {
    compact: lang === "uz" ? "Ixcham" : "Компакт",
    balanced: lang === "uz" ? "Universal" : "Универсал",
    popular: lang === "uz" ? "Mashhur" : "Популярный",
    power: lang === "uz" ? "Kuchli" : "Мощный",
    industrial: lang === "uz" ? "Industrial" : "Индустриал",
  };

  return (
    <section id="models" className="relative py-16 md:py-24 bg-ink-800/40">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-8 reveal">
            <span className="chip mb-3">MODELS · PRICES</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{T.models.h2[lang]}</h2>
            <p className="mt-4 text-muted max-w-2xl">{T.models.sub[lang]}</p>
          </div>
          <div className="lg:col-span-4 reveal">
            <div className="glass rounded-3xl p-5">
              <p className="text-sm text-white/80">{T.models.notSure[lang]}</p>
              <div className="mt-3">
                <CallBtn lang={lang} T={T} source="models_header" testId="models-header-call" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {MODELS.map((m, idx) => {
            const popular = m.tier === "popular";
            return (
              <article
                key={m.id}
                className={`reveal relative rounded-[24px] bg-ink-500 border ${popular ? "border-ice-400/40 ring-ice" : "border-white/5"} card-hover overflow-hidden flex flex-col`}
                data-testid={`model-card-${m.id}`}
              >
                {popular && (
                  <span className="absolute top-3 right-3 z-10 chip" style={{ background: "rgba(255,138,0,.12)", borderColor: "rgba(255,138,0,.4)", color: "#FFB020" }}>
                    HIT · {lang === "uz" ? "Mashhur" : "Популярный"}
                  </span>
                )}
                <div className="relative h-52 bg-gradient-to-b from-ink-700 to-ink-500 overflow-hidden">
                  <img src={m.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-500 via-transparent to-transparent" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-2xl tracking-tight">{m.id}</h3>
                    <span className="text-xs text-ice-300 font-semibold">{tierLabel[m.tier]}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted">{m.sizeHint[lang]}</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-display font-bold text-3xl text-white">${m.price}</span>
                    <span className="text-xs text-muted">{T.models.badge[lang]}</span>
                  </div>
                  <p className="mt-3 text-sm text-muted leading-relaxed">{m.desc[lang]}</p>
                  <div className="mt-5 flex flex-col gap-2">
                    <CallBtn lang={lang} T={T} source="model_card" model={m.id} testId={`model-call-${m.id}`} />
                    <a
                      href={telegramLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackEvent("ClickTelegram", { source: "model_card", model: m.id, language: lang })}
                      data-testid={`model-telegram-${m.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold hover:brightness-110"
                      style={{ background: "rgba(34,158,217,.12)", color: "#229ED9", border: "1px solid rgba(34,158,217,.35)" }}
                    >
                      <Send size={14} /> Telegram
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============ COMPARISON ============ */
function Comparison({ lang, T }) {
  const headers = T.comparison.headers[lang];
  const rows = T.comparison.rows[lang];
  return (
    <section id="comparison" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 reveal">
            <span className="chip mb-3">FAIR COMPARE</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
              {T.comparison.h2[lang]}
            </h2>
            <p className="mt-4 text-muted max-w-md">
              {lang === "uz"
                ? "Air Cooler — bu konditsioner emas. Quyida halol farqlar."
                : "Air Cooler — это не кондиционер. Ниже честные различия."}
            </p>
            <div className="mt-6 relative rounded-[28px] overflow-hidden glass">
              <img src="/images/aircooler/10.png" alt="" loading="lazy" className="w-full h-auto object-cover" />
            </div>
            <div className="mt-6">
              <CallBtn lang={lang} T={T} source="comparison" label={T.comparison.cta[lang]} testId="comparison-call-button" />
            </div>
          </div>

          <div className="lg:col-span-7 reveal w-full">
            <div className="glass rounded-[24px] p-2 md:p-3 w-full max-w-full overflow-x-auto overflow-y-hidden no-scrollbar -webkit-overflow-scrolling-touch">
              <table className="compare-table w-full min-w-[560px] text-left border-collapse">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-muted">
                    {headers.map((h, i) => (
                      <th key={i} className="py-3 px-3 md:px-4 border-b border-white/10 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr key={ri} className="text-sm">
                      {r.map((c, ci) => (
                        <td
                          key={ci}
                          className={`py-3 px-3 md:px-4 border-b border-white/5 ${
                            ci === 0 ? "text-muted" : ci === 1 ? "text-ice-300 font-semibold" : "text-white/80"
                          }`}
                        >
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ CONSULTATION BLOCK ============ */
function Consultation({ lang, T }) {
  return (
    <section id="consult" className="relative py-16 md:py-24 bg-ink-800/40">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative glass rounded-[28px] p-6 md:p-12 overflow-hidden">
          <div className="absolute -top-20 -right-16 w-[320px] h-[320px] rounded-full hero-blob opacity-50" />
          <div className="grid lg:grid-cols-12 gap-8 items-center relative">
            <div className="lg:col-span-7 reveal">
              <span className="chip mb-3">CONSULTATION</span>
              <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-tight">{T.consult.h2[lang]}</h2>
              <p className="mt-4 text-muted text-[16px] md:text-lg leading-relaxed max-w-2xl">{T.consult.sub[lang]}</p>

              <div className="mt-7 flex flex-wrap gap-3 items-center">
                <CallBtn lang={lang} T={T} source="consult" label={T.cta.callManager[lang]} big testId="consult-call-button" />
                <a
                  href={telegramLink}
                  target="_blank" rel="noreferrer"
                  onClick={() => trackEvent("ClickTelegram", { source: "consult", language: lang })}
                  data-testid="consult-telegram-button"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-4 text-base font-bold transition hover:brightness-110 shadow-lg"
                  style={{ background: "#229ED9", color: "white" }}
                >
                  <Send size={18} /> {T.cta.writeTelegram[lang]}
                </a>
              </div>
              <p className="mt-3 text-sm text-muted">{T.consult.micro[lang]}</p>
            </div>

            <div className="lg:col-span-5 reveal">
              <div className="relative rounded-[24px] overflow-hidden glass">
                <img src="/images/aircooler/6.png" alt="" loading="lazy" className="w-full h-72 md:h-96 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ BUSINESS USE CASES ============ */
function BusinessUseCases({ lang, T }) {
  const cases = [
    {
      key: "store",
      title: { ru: "Магазины", uz: "Do'konlar" },
      copy: {
        ru: "Когда покупателям жарко, они быстрее уходят. Air Cooler помогает создать более комфортную атмосферу без сложного монтажа.",
        uz: "Mijozlarga issiq bo'lsa, ular tezroq chiqib ketadi. Air Cooler murakkab montajsiz qulayroq muhit yaratishga yordam beradi.",
      },
      img: "/images/aircooler/6.png",
    },
    {
      key: "cafe",
      title: { ru: "Кафе", uz: "Kafe" },
      copy: {
        ru: "Летняя зона должна быть местом отдыха, а не испытанием жарой.",
        uz: "Yozgi hudud dam olish joyi bo'lishi kerak, issiq sinovi emas.",
      },
      img: "/images/aircooler/7.png",
    },
    {
      key: "office",
      title: { ru: "Офисы", uz: "Ofislar" },
      copy: {
        ru: "Свежий поток воздуха помогает сотрудникам легче переносить жаркий день.",
        uz: "Toza havo oqimi xodimlarga issiq kunni yengilroq o'tkazishga yordam beradi.",
      },
      img: "/images/aircooler/3.png",
    },
    {
      key: "warehouse",
      title: { ru: "Склады", uz: "Omborlar" },
      copy: {
        ru: "Для больших помещений важны мощность, мобильность и экономичность.",
        uz: "Katta joylar uchun kuch, mobillik va tejamkorlik muhim.",
      },
      img: "/images/aircooler/8.png",
    },
  ];

  return (
    <section id="business" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mb-10 max-w-2xl">
          <span className="chip mb-3">B2B USE CASES</span>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
            {lang === "uz" ? "Biznes uchun yechimlar" : "Решения для бизнеса"}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cases.map((c, i) => (
            <article key={c.key} className="reveal relative rounded-[24px] overflow-hidden bg-ink-500 border border-white/5 card-hover" data-testid={`business-case-${c.key}`}>
              <div className="grid grid-cols-1 sm:grid-cols-5 h-full">
                <div className="sm:col-span-2 relative h-40 sm:h-auto">
                  <img src={c.img} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-ink-500 via-ink-500/40 to-transparent" />
                </div>
                <div className="sm:col-span-3 p-5 flex flex-col justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold text-2xl">{c.title[lang]}</h3>
                    <p className="mt-2 text-sm text-muted leading-relaxed">{c.copy[lang]}</p>
                  </div>
                  <div className="mt-5">
                    <CallBtn
                      lang={lang} T={T} source="business" segment={c.key}
                      label={lang === "uz" ? `${c.title.uz} uchun qo'ng'iroq` : `Позвонить для ${c.title.ru.toLowerCase()}`}
                      testId={`business-call-${c.key}`}
                    />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ TRUST / DELIVERY ============ */
function Trust({ lang, T }) {
  const items = T.trust.items[lang];
  const icons = [<Warehouse size={18} />, <Truck size={18} />, <Sparkles size={18} />, <ShieldCheck size={18} />, <ShieldCheck size={18} />];
  return (
    <section id="trust" className="relative py-16 md:py-24 bg-ink-800/40">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 reveal">
            <span className="chip mb-3">DELIVERY · TRUST</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{T.trust.h2[lang]}</h2>

            <div className="mt-7 space-y-3">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-2xl px-4 py-3 card-hover"
                  data-testid={`trust-item-${i}`}
                >
                  <span className="w-10 h-10 rounded-xl grid place-items-center bg-ice-400/15 text-ice-300">
                    {icons[i] || <ShieldCheck size={18} />}
                  </span>
                  <span className="text-white/90 text-sm">{it}</span>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <CallBtn lang={lang} T={T} source="trust" label={T.cta.callDelivery[lang]} testId="trust-call-button" />
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="relative rounded-[28px] overflow-hidden glass">
              <img src="/images/aircooler/8.png" alt="" loading="lazy" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-ice-300">{lang === "uz" ? "Ombor" : "Склад"}</div>
                  <div className="font-display font-bold text-lg">{lang === "uz" ? "Toshkent" : "Ташкент"}</div>
                </div>
                <div className="text-xs text-muted text-right max-w-[60%]">
                  {lang === "uz" ? "O'zbekiston bo'ylab yetkazib berish" : "Доставка по Узбекистану"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Sections(props) {
  return (
    <>
      <UrgencyStrip {...props} />
      <Problem {...props} />
      <HowItWorks {...props} />
      <Segments {...props} />
      <Models {...props} />
      <Comparison {...props} />
      <Consultation {...props} />
      <BusinessUseCases {...props} />
      <Trust {...props} />
    </>
  );
}
